import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'nl' | 'en';

interface Translations {
  [key: string]: {
    nl: string;
    en: string;
  };
}

// All translations for the website
export const translations: Translations = {
  // Navbar
  'nav.home': { nl: 'Home', en: 'Home' },
  'nav.jobs': { nl: 'Vacatures', en: 'Jobs' },
  'nav.about': { nl: 'Over ons', en: 'About Us' },
  'nav.contact': { nl: 'Contact', en: 'Contact' },
  'nav.sendCV': { nl: 'Stuur je CV', en: 'Send Your CV' },
  'nav.whoWeAre': { nl: 'Wie zijn wij', en: 'Who We Are' },
  'nav.whatWeDo': { nl: 'Wat wij doen', en: 'What We Do' },
  'nav.ourTeam': { nl: 'Ons Team', en: 'Our Team' },
  'nav.ourPartners': { nl: 'Onze Partners', en: 'Our Partners' },

  // Hero
  'hero.title1': { nl: 'Zoek jouw', en: 'Find your' },
  'hero.title2': { nl: 'uitdaging', en: 'challenge' },
  'hero.subtitle': { nl: 'Jouw Vertrouwde Partner in Finance Recruitment', en: 'Your Trusted Partner in Finance Recruitment' },
  'hero.search': { nl: 'Zoek vacature...', en: 'Search job...' },
  'hero.searchBtn': { nl: 'Vind jouw droombaan', en: 'Find your dream job' },

  // Sectors/Services
  'sectors.title': { nl: 'Onze Expertises', en: 'Our Sectors' },
  'sectors.accounting': { nl: 'Accounting', en: 'Accounting' },
  'sectors.tax': { nl: 'Tax', en: 'Tax' },
  'sectors.audit': { nl: 'Audit', en: 'Audit' },
  'sectors.legal': { nl: 'Legal', en: 'Legal' },

  // Why Choose Us
  'why.title': { nl: 'Waarom kiezen voor Meijer & Knijnenberg?', en: 'Why Choose Meijer & Knijnenberg?' },
  'why.description': { 
    nl: 'Met meer dan 8 jaar expertise bieden wij hoogwaardige en betrouwbare dienstverlening. Onze aanpak is persoonlijk, we luisteren en denken met je mee.', 
    en: 'With over 8 years of expertise, we offer high-quality and reliable services. Our approach is personal - we listen and think along with you.' 
  },
  'why.noCure': { nl: '100% No Cure, No Pay', en: '100% No Cure, No Pay' },
  'why.noCureDesc': { nl: 'Geen resultaat, geen kosten.', en: 'No result, no costs.' },
  'why.personal': { nl: 'Persoonlijke Aanpak', en: 'Personal Approach' },
  'why.personalDesc': { nl: 'Wij luisteren en zorgen voor maatwerkoplossingen.', en: 'We listen and provide customized solutions.' },
  'why.available': { nl: 'Altijd Bereikbaar', en: 'Always Available' },
  'why.availableDesc': { nl: 'Op elk moment kun je op ons rekenen.', en: 'You can count on us at any time.' },

  // Latest Roles
  'roles.title': { nl: 'Recente Vacatures', en: 'Latest Roles' },
  'roles.viewAll': { nl: 'Bekijk alle vacatures', en: 'View all vacancies' },

  // Contact
  'contact.title': { nl: 'Neem Contact Op', en: 'Get in Touch' },
  'contact.subtitle': { nl: 'Ben jij klaar voor een nieuwe uitdaging?', en: 'Ready for a new challenge?' },
  'contact.phone': { nl: 'Telefoon', en: 'Phone' },
  'contact.email': { nl: 'E-mail', en: 'Email' },
  'contact.address': { nl: 'Adres', en: 'Address' },

  // Footer
  'footer.rights': { nl: '© 2025 Meijer & Knijnenberg. Alle rechten voorbehouden.', en: '© 2025 Meijer & Knijnenberg. All Rights Reserved.' },
  'footer.privacy': { nl: 'Privacy Policy', en: 'Privacy Policy' },
  'footer.cookies': { nl: 'Cookie Policy', en: 'Cookie Policy' },
  'footer.company': { nl: 'Bedrijf', en: 'Company' },
  'footer.support': { nl: 'Support', en: 'Support' },
  'footer.whatWeDo': { nl: 'Wat wij doen', en: 'What We Do' },
  'footer.about': { nl: 'Over Ons', en: 'About' },
  'footer.successStories': { nl: 'Succesverhalen', en: 'Success Stories' },
  'footer.vacancies': { nl: 'Vacatures', en: 'Vacancies' },
  'footer.faqs': { nl: 'Veelgestelde Vragen', en: 'FAQs' },
  'footer.resources': { nl: 'Bronnen', en: 'Resources' },
  'footer.viewAll': { nl: 'Bekijk alles', en: 'View All' },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('nl'); // Dutch as default

  const t = (key: string): string => {
    const translation = translations[key];
    if (translation) {
      return translation[language];
    }
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

